'use client';
import React, { useState } from 'react';

import PublicNav from '@/components/PublicNav';

export default function ContactPage() {
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <div className="min-h-screen bg-background">
      <PublicNav activePage="contact" />

      {/* Hero */}
      <section className="pt-16 pb-16 px-4 sm:px-6 bg-navy-light border-b border-border/40">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-[640px]">
            <p className="text-[11px] font-700 text-accent uppercase tracking-widest mb-3">Get in Touch</p>
            <h1 className="text-4xl sm:text-5xl font-bold text-primary mb-6 leading-tight">Contact us</h1>
            <p className="text-[15px] sm:text-[16px] text-muted-foreground leading-relaxed">
              Whether you have a question about opening an account, a query about an existing custody arrangement, or a compliance matter — our team is here to help.
            </p>
          </div>
        </div>
      </section>

      {/* Contact content */}
      <section className="py-16 sm:py-20 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12">
            {/* Form — wider */}
            <div className="lg:col-span-3">
              <h2 className="text-xl font-bold text-primary mb-6">Send us a message</h2>
              {submitted ? (
                <div className="card-elevated p-8 sm:p-10 rounded-2xl text-center">
                  <div className="w-14 h-14 rounded-full bg-success/10 flex items-center justify-center mx-auto mb-5">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path d="M5 13l4 4L19 7" stroke="#16A34A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <h3 className="text-[17px] font-bold text-primary mb-2">Message received</h3>
                  <p className="text-[14px] text-muted-foreground">We will get back to you within one business day. For urgent matters, please call us directly on +44 7887 533716.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  {/* Name + email stack on mobile, side-by-side on sm+ */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-[12px] font-600 text-foreground mb-1.5">Full name</label>
                      <input
                        type="text"
                        required
                        value={form.name}
                        onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                        className="w-full px-4 py-3 rounded-lg border border-border bg-background text-[14px] text-foreground focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent transition-colors min-h-[48px]"
                        placeholder="Your full name"
                      />
                    </div>
                    <div>
                      <label className="block text-[12px] font-600 text-foreground mb-1.5">Email address</label>
                      <input
                        type="email"
                        required
                        value={form.email}
                        onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                        className="w-full px-4 py-3 rounded-lg border border-border bg-background text-[14px] text-foreground focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent transition-colors min-h-[48px]"
                        placeholder="you@example.com"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-[12px] font-600 text-foreground mb-1.5">Subject</label>
                    <select
                      value={form.subject}
                      onChange={e => setForm(f => ({ ...f, subject: e.target.value }))}
                      className="w-full px-4 py-3 rounded-lg border border-border bg-background text-[14px] text-foreground focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent transition-colors min-h-[48px]"
                    >
                      <option value="">Select a topic</option>
                      <option value="account">Opening an account</option>
                      <option value="custody">Asset custody enquiry</option>
                      <option value="withdrawal">Withdrawal or delivery</option>
                      <option value="compliance">Compliance matter</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-[12px] font-600 text-foreground mb-1.5">Message</label>
                    <textarea
                      required
                      rows={5}
                      value={form.message}
                      onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
                      className="w-full px-4 py-3 rounded-lg border border-border bg-background text-[14px] text-foreground focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent transition-colors resize-none"
                      placeholder="Tell us what you need help with..."
                    />
                  </div>
                  <button
                    type="submit"
                    className="w-full sm:w-auto vault-gradient text-white font-600 px-7 py-3.5 rounded-xl text-[14px] hover:opacity-90 transition-all active:scale-[0.98] min-h-[48px]"
                  >
                    Send message
                  </button>
                </form>
              )}
            </div>

            {/* Contact details */}
            <div className="lg:col-span-2 space-y-5">
              <div className="card-elevated p-6 sm:p-7 rounded-2xl">
                <h3 className="text-[15px] font-bold text-primary mb-5">Contact details</h3>
                <div className="space-y-4">
                  {[
                    { icon: '📍', label: 'Registered Office', value: '272 Bath Street, Glasgow G2 4JR, Scotland' },
                    { icon: '📞', label: 'Telephone', value: '+44 7887 533716' },
                    { icon: '✉️', label: 'Email', value: 'karspostasi@mail.com' },
                  ].map(item => (
                    <div key={item.label} className="flex items-start gap-3">
                      <span className="text-lg flex-shrink-0 mt-0.5">{item.icon}</span>
                      <div>
                        <p className="text-[11px] font-700 text-muted-foreground uppercase tracking-wide mb-0.5">{item.label}</p>
                        <p className="text-[13px] text-foreground">{item.value}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="card-elevated p-6 sm:p-7 rounded-2xl border border-primary/10 bg-navy-light">
                <h3 className="text-[15px] font-bold text-primary mb-3">Response times</h3>
                <div className="space-y-3">
                  {[
                    { type: 'General enquiries', time: 'Within 1 business day' },
                    { type: 'Account applications', time: 'Within 2 business days' },
                    { type: 'Compliance matters', time: 'Within 3 business days' },
                    { type: 'Urgent matters', time: 'Call us directly' },
                  ].map(item => (
                    <div key={item.type} className="flex items-center justify-between py-2 border-b border-border/40 last:border-0">
                      <span className="text-[12px] text-foreground">{item.type}</span>
                      <span className="text-[11px] font-600 text-muted-foreground">{item.time}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="p-5 rounded-xl bg-muted/30 border border-border/50">
                <p className="text-[12px] font-700 text-foreground mb-1">Company registration</p>
                <p className="text-[12px] text-muted-foreground">Registered in Scotland, Number SC756088</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-primary text-white/60 py-10 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4 text-center md:text-left">
          <div className="flex items-center gap-3">
            <div className="w-7 h-7 rounded-lg gold-gradient flex items-center justify-center">
              <svg width="14" height="14" viewBox="0 0 22 22" fill="none">
                <circle cx="11" cy="11" r="9" stroke="white" strokeWidth="2"/>
                <path d="M8 7l3 4 3-4M11 11v5" stroke="white" strokeWidth="1.8" strokeLinecap="round"/>
              </svg>
            </div>
            <span className="text-white font-bold text-[14px]">KarsPostasi Ltd</span>
          </div>
          <p className="text-[11px]">Registered in Scotland, Number SC756088 · 272 Bath Street, Glasgow G2 4JR</p>
          <p className="text-[11px]">© 2026 KarsPostasi Ltd. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
